import DocumentAssociation from '@core/classes/DocumentAssociation';
import { PlaidBankAccount } from '@core/classes/Plaid/PlaidBankAccount';

/** Contains all Plaid tokens that are associated with a customer. */
export class PlaidCustomerInfo {
	/** The ElasticSearch ID of this record. */
	id: string = null;

	/** The Saturn ID of this customer. */
	customerId: string = null;

	/** Uses ENUMS.DOC_ASSOCIATION. The type of customer record the customerID refers to. Will almost always be a Customer record. */
	customerIdType?: number = null;

	/** A list of Asset Report tokens associated with this customer. */
	assetReportTokens: PlaidAssetReportToken[] = [];

	/** The User Token associated with the customer. */
	userToken?: PlaidUserToken = null;

	/** A list of Access Tokens associated with this customer. */
	accessTokens: PlaidAccessToken[] = [];

	/** The date this record was initially created. */
	dateCreated?: Date = null;

	constructor(init?: Partial<PlaidCustomerInfo>) {
		this.id = init?.id;
		this.customerId = init?.customerId;
		this.customerIdType = init?.customerIdType;
		this.assetReportTokens = init?.assetReportTokens?.map(t => new PlaidAssetReportToken(t)) ?? [];
		this.userToken = init?.userToken ? new PlaidUserToken(init.userToken) : null;
		this.accessTokens = init?.accessTokens?.map(t => new PlaidAccessToken(t)) ?? [];
		this.dateCreated = init?.dateCreated;
	}
}

// =============
// TOKEN CLASSES
// =============

export class PlaidToken {
	/** The date this token was initially created. */
	dateCreated: Date = null;

	/** The Plaid environment this token was created in. Tokens made in one environment cannot be used in another. */
	plaidEnvironment?: 'SANDBOX' | 'PRODUCTION' = null;

	/** The code of the store that originally created this token. */
	storeCode?: string = null;

	constructor(init?: Partial<PlaidToken>) {
		this.dateCreated = init?.dateCreated;
		this.plaidEnvironment = init?.plaidEnvironment;
		this.storeCode = init?.storeCode;
	}
}

/** Used to make Plaid API requests related to a specific Asset Report. */
export class PlaidAssetReportToken extends PlaidToken {
	/** Plaid's internal ID for the Asset Report */
	assetReportId: string = null;

	/** The maximum number of days of history included in the Asset Report. */
	daysRequested: number = 0;

	/** The current status (ex. active, revoked, expired) of the Access Token. Uses ENUMS.TOKEN_STATUS */
	status?: number = null;

	/** A list of Access Tokens the Asset Report is associated with. */
	associatedAccessTokens: DocumentAssociation[] = [];

	constructor(init?: Partial<PlaidAssetReportToken>) {
		super(init);
		this.assetReportId = init?.assetReportId;
		this.daysRequested = init?.daysRequested ?? 0;
		this.status = init?.status;
		this.associatedAccessTokens = init?.associatedAccessTokens ?? [];
	}
}

/** Used when working with Plaid's Income or Employement APIs.
 *  Used to associate income data from multiple sources with a single user. Non-expiring.
 * */
export class PlaidUserToken extends PlaidToken {
	/** Plaid's internal ID for this User Token. */
	userId: string = null;

	/** A list of Access Tokens this token is associated with. */
	associatedAccessTokens: DocumentAssociation[] = [];

	constructor(init?: Partial<PlaidUserToken>) {
		super(init);
		this.userId = init?.userId;
		this.associatedAccessTokens = init?.associatedAccessTokens ?? [];
	}
}

/** Used to make Plaid API requests related to a specific Item (a bank account). Also contains metadata about the Item. */
export class PlaidAccessToken extends PlaidToken {
	/** Plaid's internal ID that identifies a Plaid Item. */
	plaidItemId: string = null;

	/** The current status (ex. active, revoked, expired) of the Access Token. Uses ENUMS.TOKEN_STATUS */
	status?: number = null;

	/** A list of Plaid products currently enabled on this Access Token. Uses ENUMS.PLAID_PRODUCTS */
	activeProducts: string[] = [];

	/** A list of Plaid products currently being billed for on this Access Token. Uses ENUMS.PLAID_PRODUCTS */
	billedProducts: string[] = [];

	/** The institution this Access Token is associated with */
	institution?: PlaidInstitution = null;

	/** A list of accounts this Access Token has access to.*/
	accounts: PlaidBankAccount[] = [];

	/** The date this token was last updated */
	dateLastUpdated: Date = null;

	constructor(init?: Partial<PlaidAccessToken>) {
		super(init);
		this.plaidItemId = init?.plaidItemId;
		this.status = init?.status;
		this.activeProducts = init?.activeProducts ?? [];
		this.billedProducts = init?.billedProducts ?? [];
		this.institution = init?.institution;
		this.accounts = init?.accounts;
		this.dateLastUpdated = init?.dateLastUpdated;
	}

	/**
	 * Returns whether the given product is active on this access token.
	 * @param {string} product Use ENUMS.PLAID_PRODUCTS
	 * @return {*}  {boolean}
	 * @memberof PlaidAccessToken
	 */
	hasProduct(product: string): boolean {
		return this.activeProducts.includes(product);
	}

	/**
	 * Returns whether the given product is being billed on this access token
	 * @param {string} product Use ENUMS.PLAID_PRODUCTS
	 * @return {*}  {boolean}
	 * @memberof PlaidAccessToken
	 */
	isProductBilled(product: string): boolean {
		return this.billedProducts.includes(product);
	}
}

export class PlaidInstitution {
	/** The user-friendly name of the institution */
	name: string = null;

	/** Plaid's internal ID for this institution */
	id: string = null;

	constructor(init?: PlaidInstitution) {
		this.name = init?.name;
		this.id = init?.id;
	}
}
